/**
 * Contractor Form Component
 * Modal form for creating and editing contractors
 */

import '@/modules/utility/ds-wms/styles/wmsTable.css';
import { useState, useEffect, useCallback } from 'react';
import { contractorsApi, mastersApi } from '../../services/wmsService';
import { usePwdClasses } from '../../hooks/useSetupMasters';
import { useToast } from '@/hooks/useToast';
import DocumentUploader from '../DocumentUploader';
import { ListSkeleton } from '@/components/base/Skeleton';
import { WMS_POPUP_HEADER, WMS_POPUP_TITLE, WMS_POPUP_SUBTITLE, WMS_POPUP_ICON, WMS_POPUP_CLOSE } from '../common/wmsPopupHeader';

interface ContractorFormData {
  cntrctr_cd: string;
  cntrctr_nm: string;
  cntrctr_ctgry_id: number | null;
  cntrctr_sts_id: number | null;
  frm_type_id: number | null;
  frm_nm: string;
  frm_addr_tx: string;
  estblshmnt_dt: string;
  cntct_prsn_nm: string;
  addr_tx: string;
  cty_nm: string;
  stte_nm: string;
  pin_cd: string;
  phn_nu: string;
  eml_tx: string;
  rgstrn_nu: string;
  rgstrn_dt: string;
  rgstrn_vld_dt: string;
  pwdclass_cd: string;
  gstn_nu: string;
  pan_nu: string;
  bnk_acnt_hldr_nm: string;
  bnk_acnt_nu: string;
  ifsc_cd: string;
  bnk_nm: string;
  bnk_brnch_nm: string;
  bnk_acnt_type_cd: string;
  msme_rgstrd_in: boolean;
  udyam_rgstrn_nu: string;
  max_tndr_cpcty_at: number;
  avg_trnvr_at: number;
}

interface ContractorCategory {
  cntrctr_ctgry_id: number;
  cntrctr_ctgry_cd: string;
  cntrctr_ctgry_nm: string;
}

interface ContractorStatus {
  cntrctr_sts_id: number;
  cntrctr_sts_cd: string;
  cntrctr_sts_nm: string;
}

interface FirmType {
  frm_type_id: number;
  frm_type_cd: string;
  frm_type_nm: string;
}

interface FirmSpecialization {
  frm_spclzn_id: number;
  frm_spclzn_cd: string;
  frm_spclzn_nm: string;
}

interface ContractorItem {
  cntrctr_itm_id?: number;
  itm_cd: string;
  itm_nm: string;
  itm_type_cd: 'SERVICE' | 'MACHINERY' | 'GOODS';
  uom_cd: string;
  rate_at: number;
  sor_itm_id?: number | null;
  sor_itm_cd?: string;
  dscn_tx?: string;
}

interface SORItem {
  sor_itm_id: number;
  sor_itm_cd: string;
  sor_itm_nm: string;
  uom_cd: string;
  rate_at: number;
}

interface ContractorContact {
  cntrctr_cntct_id?: number;
  cntrctr_id?: number;
  cntct_nm: string;
  dsgntn_cd: string;
  dsgntn_nm: string;
  phn_nu: string;
  alt_phn_nu?: string;
  eml_tx?: string;
  is_prmry_in: boolean;
}

interface ContractorFormProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Called after a successful save. On CREATE the new contractor row is passed so the
   * opener can act on it directly — the tender Participants panel adds it as a bidder
   * rather than sending the user back to a picker to hunt for it. Optional, so the
   * five existing `onSuccess={() => ...}` callers are unaffected.
   */
  onSuccess: (created?: Record<string, any>) => void;
  contractorId?: number | null;
}

const ITEM_TYPES = [
  { code: 'SERVICE', label: 'Service', icon: 'ri-service-line' },
  { code: 'MACHINERY', label: 'Machinery', icon: 'ri-truck-line' },
  { code: 'GOODS', label: 'Goods', icon: 'ri-box-3-line' }
];

const COMMON_UOMS = [
  { code: 'NOS', label: 'Numbers' },
  { code: 'SQM', label: 'Square Meters' },
  { code: 'RMT', label: 'Running Meters' },
  { code: 'CUM', label: 'Cubic Meters' },
  { code: 'KG', label: 'Kilograms' },
  { code: 'MT', label: 'Metric Tons' },
  { code: 'LTR', label: 'Litres' },
  { code: 'DAY', label: 'Per Day' },
  { code: 'HOUR', label: 'Per Hour' },
  { code: 'MONTH', label: 'Per Month' },
  { code: 'LS', label: 'Lump Sum' },
  { code: 'SET', label: 'Set' }
];

const emptyItem: ContractorItem = {
  itm_cd: '',
  itm_nm: '',
  itm_type_cd: 'SERVICE',
  uom_cd: 'NOS',
  rate_at: 0,
  sor_itm_id: null,
  dscn_tx: ''
};

const CONTACT_DESIGNATIONS = [
  { code: 'PROPRIETOR', label: 'Proprietor' },
  { code: 'DIRECTOR', label: 'Director' },
  { code: 'CEO', label: 'CEO (Chief Executive Officer)' },
  { code: 'MD', label: 'MD (Managing Director)' },
  { code: 'CHAIRMAN', label: 'Chairman' },
  { code: 'PARTNER', label: 'Partner' },
  { code: 'MANAGER', label: 'Manager' },
  { code: 'ACCOUNTANT', label: 'Accountant' },
  { code: 'SITE_ENGINEER', label: 'Site Engineer' },
  { code: 'PROJECT_MANAGER', label: 'Project Manager' },
  { code: 'AUTHORIZED_SIGNATORY', label: 'Authorized Signatory' },
  { code: 'OTHER', label: 'Other' }
];

const emptyContact: ContractorContact = {
  cntct_nm: '',
  dsgntn_cd: '',
  dsgntn_nm: '',
  phn_nu: '',
  alt_phn_nu: '',
  eml_tx: '',
  is_prmry_in: false
};

// GSTIN State Codes
const GSTIN_STATE_CODES: Record<string, string> = {
  '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh',
  '05': 'Uttarakhand', '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan',
  '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim', '12': 'Arunachal Pradesh',
  '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura',
  '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand',
  '21': 'Odisha', '22': 'Chhattisgarh', '23': 'Madhya Pradesh', '24': 'Gujarat',
  '26': 'Dadra & Nagar Haveli and Daman & Diu', '27': 'Maharashtra', '29': 'Karnataka',
  '30': 'Goa', '31': 'Lakshadweep', '32': 'Kerala', '33': 'Tamil Nadu',
  '34': 'Puducherry', '35': 'Andaman & Nicobar Islands', '36': 'Telangana',
  '37': 'Andhra Pradesh', '38': 'Ladakh'
};

// Get state info from GSTIN
function getGSTINStateInfo(gstin: string): { stateName: string; isAP: boolean } | null {
  if (!gstin || gstin.length < 2) return null;
  const stateCode = gstin.substring(0, 2);
  const stateName = GSTIN_STATE_CODES[stateCode];
  if (!stateName) return null;
  return { stateName, isAP: stateCode === '37' };
}

const initialFormData: ContractorFormData = {
  cntrctr_cd: '',
  cntrctr_nm: '',
  cntrctr_ctgry_id: null,
  cntrctr_sts_id: null,
  frm_type_id: null,
  frm_nm: '',
  frm_addr_tx: '',
  estblshmnt_dt: '',
  cntct_prsn_nm: '',
  addr_tx: '',
  cty_nm: '',
  stte_nm: 'Andhra Pradesh',
  pin_cd: '',
  phn_nu: '',
  eml_tx: '',
  rgstrn_nu: '',
  rgstrn_dt: '',
  rgstrn_vld_dt: '',
  pwdclass_cd: '',
  gstn_nu: '',
  pan_nu: '',
  bnk_acnt_hldr_nm: '',
  bnk_acnt_nu: '',
  ifsc_cd: '',
  bnk_nm: '',
  bnk_brnch_nm: '',
  bnk_acnt_type_cd: 'CURRENT',
  msme_rgstrd_in: false,
  udyam_rgstrn_nu: '',
  max_tndr_cpcty_at: 0,
  avg_trnvr_at: 0
};

// Same rules the server applies (ds-wms/utils/payee.utils.js).
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;
const IFSC_RE = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const ACNT_RE = /^[0-9]{9,18}$/;
const ACCOUNT_TYPES = [
  { value: 'CURRENT', label: 'Current' },
  { value: 'SAVINGS', label: 'Savings' },
  { value: 'CASH_CREDIT', label: 'Cash Credit' },
  { value: 'OVERDRAFT', label: 'Overdraft' },
];

// Section Header Component
function SectionHeader({ icon, title }: { icon: string; title: string }) {
  return (
    <div className="flex items-center gap-2 mb-3 pb-2 border-b border-gray-200">
      <i className={`${icon} text-blue-600`}></i>
      <h4 className="font-medium text-gray-800">{title}</h4>
    </div>
  );
}

export default function ContractorForm({ isOpen, onClose, onSuccess, contractorId }: ContractorFormProps) {
  const { success: showSuccess, error: showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // PWD classes come from Setup -> PWD Classes (dswms_pwd_cls_lst_t). This list used to
  // be a const in this file and a different const in ContractorSelector.tsx.
  const pwdClasses = usePwdClasses();
  const [formData, setFormData] = useState<ContractorFormData>(initialFormData);
  const [categories, setCategories] = useState<ContractorCategory[]>([]);
  const [statuses, setStatuses] = useState<ContractorStatus[]>([]);
  const [firmTypes, setFirmTypes] = useState<FirmType[]>([]);
  const [specializations, setSpecializations] = useState<FirmSpecialization[]>([]);
  const [selectedSpecializations, setSelectedSpecializations] = useState<number[]>([]);
  const [activeTab, setActiveTab] = useState<'details' | 'services' | 'contacts' | 'documents'>('details');

  // Contacts state
  const [contractorContacts, setContractorContacts] = useState<ContractorContact[]>([]);
  const [showContactForm, setShowContactForm] = useState(false);
  const [editingContact, setEditingContact] = useState<ContractorContact | null>(null);
  const [contactFormData, setContactFormData] = useState<ContractorContact>(emptyContact);

  // Services/Items state
  const [contractorItems, setContractorItems] = useState<ContractorItem[]>([]);
  const [sorItems, setSorItems] = useState<SORItem[]>([]);
  const [loadingSOR, setLoadingSOR] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [editingItem, setEditingItem] = useState<ContractorItem | null>(null);
  const [itemFormData, setItemFormData] = useState<ContractorItem>(emptyItem);
  const [showSORPicker, setShowSORPicker] = useState(false);
  const [sorSearchTerm, setSorSearchTerm] = useState('');

  const isEdit = !!contractorId;
  // The account number typed a second time; never sent to the server.
  const [confirmAcnt, setConfirmAcnt] = useState('');

  // Load master data
  useEffect(() => {
    const loadMasters = async () => {
      const [catResult, firmResult, specResult, stsResult] = await Promise.allSettled([
        mastersApi.getContractorCategories(),
        mastersApi.getFirmTypes?.(),
        mastersApi.getFirmSpecializations?.(),
        mastersApi.getContractorStatuses?.()
      ]);
      setCategories(catResult.status === 'fulfilled' ? catResult.value || [] : []);
      setFirmTypes(firmResult.status === 'fulfilled' ? firmResult.value || [] : []);
      setSpecializations(specResult.status === 'fulfilled' ? specResult.value || [] : []);
      // Statuses must come from dswms_cntrctr_sts_lst_t — the ids are assigned by
      // that table's serial (ACTIVE is 5 in ds_dtcp), so the old hardcoded 1-4 list
      // wrote a cntrctr_sts_id that joined to nothing and left Status blank.
      const stsRows = stsResult.status === 'fulfilled' ? stsResult.value || [] : [];
      setStatuses(stsRows);
      if (stsResult.status === 'rejected') console.warn('Failed to load contractor statuses:', stsResult.reason?.message);
      if (catResult.status === 'rejected') console.warn('Failed to load categories:', catResult.reason?.message);
      if (firmResult.status === 'rejected') console.warn('Failed to load firm types:', firmResult.reason?.message);
      if (specResult.status === 'rejected') console.warn('Failed to load specializations:', specResult.reason?.message);
    };
    loadMasters();
  }, []);

  // Load contractor data if editing
  const loadContractor = useCallback(async () => {
    if (!contractorId) {
      setFormData(initialFormData);
      setConfirmAcnt('');
      setSelectedSpecializations([]);
      setContractorItems([]);
      setContractorContacts([]);
      return;
    }

    setLoading(true);
    try {
      // Load each independently so one failure doesn't block others.
      //
      // getItems is NOT called. Its table was never ported: contractorItems.service.js
      // queries wms_cntrctr_itm_lst_t and wms_sor_itm_lst_t, and neither exists under
      // the old wms_ name or the ported dswms_ one — migration 001's rename map lists
      // four cntrctr tables and this is not among them. Every call returned
      //   relation "ds_dtcp.wms_cntrctr_itm_lst_t" does not exist
      //
      // allSettled kept the form working, but the axios interceptor raises a toast on
      // any 500 before this code sees it (interceptor.ts:200), so opening ANY
      // contractor for edit produced an "Internal server error" popup that explained
      // nothing and implicated the whole form rather than one tab.
      //
      // Restore this call and the Services/Items tab together once
      // dswms_cntrctr_itm_lst_t exists. See the tabs array below.
      const [dataResult, specsResult, contactsResult] = await Promise.allSettled([
        contractorsApi.getById(contractorId),
        mastersApi.getContractorSpecializations?.(contractorId),
        contractorsApi.getContacts?.(contractorId)
      ]);
      const itemsResult = { status: 'fulfilled' as const, value: [] as ContractorItem[] };

      const data = dataResult.status === 'fulfilled' ? dataResult.value : null;
      const contractorSpecs = specsResult.status === 'fulfilled' ? specsResult.value : [];
      const items = itemsResult.status === 'fulfilled' ? itemsResult.value : [];
      const contacts = contactsResult.status === 'fulfilled' ? contactsResult.value : [];

      if (specsResult.status === 'rejected') console.warn('Failed to load specializations:', specsResult.reason?.message);
      // No items check: itemsResult is a fixed fulfilled stub, never rejected. Restore
      // this line alongside the getItems call above when the table exists.
      if (contactsResult.status === 'rejected') console.warn('Failed to load contacts:', contactsResult.reason?.message);

      if (data) {
        setFormData({
          cntrctr_cd: data.cntrctr_cd || '',
          cntrctr_nm: data.cntrctr_nm || '',
          cntrctr_ctgry_id: data.cntrctr_ctgry_id || null,
          cntrctr_sts_id: data.cntrctr_sts_id || null,
          frm_type_id: data.frm_type_id || null,
          frm_nm: data.frm_nm || '',
          frm_addr_tx: data.frm_addr_tx || '',
          estblshmnt_dt: data.estblshmnt_dt ? data.estblshmnt_dt.split('T')[0] : '',
          cntct_prsn_nm: data.cntct_prsn_nm || '',
          addr_tx: data.addr_tx || '',
          cty_nm: data.cty_nm || '',
          stte_nm: data.stte_nm || 'Andhra Pradesh',
          pin_cd: data.pin_cd || '',
          phn_nu: data.phn_nu || '',
          eml_tx: data.eml_tx || '',
          rgstrn_nu: data.rgstrn_nu || '',
          rgstrn_dt: data.rgstrn_dt ? data.rgstrn_dt.split('T')[0] : '',
          rgstrn_vld_dt: data.rgstrn_vld_dt ? data.rgstrn_vld_dt.split('T')[0] : '',
          pwdclass_cd: data.pwdclass_cd || '',
          gstn_nu: data.gstn_nu || '',
          pan_nu: data.pan_nu || '',
          bnk_acnt_hldr_nm: data.bnk_acnt_hldr_nm || '',
          bnk_acnt_nu: data.bnk_acnt_nu || '',
          ifsc_cd: data.ifsc_cd || '',
          bnk_nm: data.bnk_nm || '',
          bnk_brnch_nm: data.bnk_brnch_nm || '',
          bnk_acnt_type_cd: data.bnk_acnt_type_cd || 'CURRENT',
          msme_rgstrd_in: data.msme_rgstrd_in || false,
          udyam_rgstrn_nu: data.udyam_rgstrn_nu || '',
          max_tndr_cpcty_at: data.max_tndr_cpcty_at || 0,
          avg_trnvr_at: data.avg_trnvr_at || 0
        });

        setConfirmAcnt(data.bnk_acnt_nu || '');

        const specIds = (contractorSpecs || []).map((s: any) => s.frm_spclzn_id);
        setSelectedSpecializations(specIds);
        setContractorItems(items || []);
        setContractorContacts(contacts || []);
      }
    } catch (error) {
      console.error('Error loading contractor:', error);
      showError('Failed to load contractor data');
    } finally {
      setLoading(false);
    }
  }, [contractorId]);

  useEffect(() => {
    if (isOpen) {
      loadContractor();
      setActiveTab('details');
    }
  }, [isOpen, loadContractor]);

  const handleInputChange = (field: keyof ContractorFormData, value: string | number | boolean | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecializationToggle = (specId: number) => {
    setSelectedSpecializations(prev => {
      if (prev.includes(specId)) {
        return prev.filter(id => id !== specId);
      } else {
        return [...prev, specId];
      }
    });
  };

  const validateForm = (): boolean => {
    if (!formData.cntrctr_nm.trim()) {
      showError('Contractor name is required');
      setActiveTab('details');
      return false;
    }
    // PAN and bank details: required for a new contractor, and checked for format
    // whenever they are filled in. Their bills are paid into this account.
    const fail = (msg: string) => { showError(msg); setActiveTab('details'); return false; };
    const pan = formData.pan_nu.trim();
    const acnt = formData.bnk_acnt_nu.replace(/\s+/g, '');
    const ifsc = formData.ifsc_cd.trim();
    if (!isEdit) {
      if (!pan) return fail('PAN number is required');
      if (!formData.bnk_acnt_hldr_nm.trim()) return fail('Account holder name is required');
      if (!acnt) return fail('Bank account number is required');
      if (!ifsc) return fail('IFSC code is required');
      if (!formData.bnk_nm.trim()) return fail('Bank name is required');
    }
    if (pan && !PAN_RE.test(pan)) return fail('PAN must be in the form AAAAA9999A');
    if (acnt && !ACNT_RE.test(acnt)) return fail('Bank account number must be 9 to 18 digits');
    if (acnt && acnt !== confirmAcnt.replace(/\s+/g, '')) return fail('The two bank account numbers do not match');
    if (ifsc && !IFSC_RE.test(ifsc)) return fail('IFSC must be 11 characters, e.g. SBIN0001234');
    /* NO CONTACT REQUIREMENT ON EDIT.
     *
     * This used to refuse every edit of a contractor that had no row in
     * dswms_cntrctr_cntct_lst_t — and 149 of the 152 live contractors have none,
     * because create has never asked for a contact. The result was that almost no
     * contractor could be edited at all: Update Contractor raised a toast, jumped to
     * the Contacts tab and sent no PUT, so bank details, PAN and tender capacity
     * silently could not be corrected.
     *
     * A rule the create path does not enforce must not be enforced here, or a row
     * legitimately saved without a contact becomes permanently uneditable. Contacts
     * stay editable on their own tab; they are simply not a precondition for saving
     * the rest of the record. */
    return true;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload = {
        ...formData,
        max_tndr_cpcty_at: parseFloat(String(formData.max_tndr_cpcty_at)) || 0,
        avg_trnvr_at: parseFloat(String(formData.avg_trnvr_at)) || 0
      };

      let savedContractorId = contractorId;
      // The created row, handed back through onSuccess. It was discarded before, so a
      // caller that had just created a contractor had no way to reference it.
      let createdContractor: Record<string, any> | undefined;

      if (isEdit && contractorId) {
        await contractorsApi.update(contractorId, payload);
        showSuccess('Contractor updated successfully');
      } else {
        const result = await contractorsApi.create(payload);
        savedContractorId = result?.cntrctr_id;
        createdContractor = result || undefined;
        showSuccess('Contractor created successfully');
      }

      // Save specializations if we have a contractor ID
      if (savedContractorId && mastersApi.saveContractorSpecializations) {
        try {
          await mastersApi.saveContractorSpecializations(savedContractorId, selectedSpecializations);
        } catch (specError) {
          console.error('Error saving specializations:', specError);
        }
      }

      // Save contractor items if we have any
      if (savedContractorId && contractorItems.length > 0 && contractorsApi.saveItems) {
        try {
          await contractorsApi.saveItems(savedContractorId, contractorItems);
        } catch (itemsError) {
          console.error('Error saving contractor items:', itemsError);
        }
      }

      // Save contractor contacts if we have any
      if (savedContractorId && contractorContacts.length > 0 && contractorsApi.saveContacts) {
        try {
          await contractorsApi.saveContacts(savedContractorId, contractorContacts);
        } catch (contactsError) {
          console.error('Error saving contractor contacts:', contactsError);
        }
      }

      onSuccess(createdContractor);
      onClose();
    } catch (error: any) {
      console.error('Error saving contractor:', error);
      showError(error.response?.data?.message || 'Failed to save contractor');
    } finally {
      setSaving(false);
    }
  };

  // Services/Items is hidden, not deleted. Its storage was never ported —
  // dswms_cntrctr_itm_lst_t does not exist, so all five contractor-item endpoints
  // fail. The tab rendered normally and showed "No items added yet", which reads as
  // "this contractor has none" rather than "the query failed"; anything entered there
  // was lost on save. Restore this entry and the getItems call in loadContractor
  // together once the table exists.
  const tabs = [
    { key: 'details', label: 'Details', icon: 'ri-file-list-3-line' },
    { key: 'contacts', label: 'Contacts', icon: 'ri-contacts-line' },
    { key: 'documents', label: 'Documents', icon: 'ri-folder-line' }
  ];

  // Item form handlers
  const handleAddItem = () => {
    setEditingItem(null);
    setItemFormData(emptyItem);
    setShowItemForm(true);
  };

  const handleEditItem = (item: ContractorItem) => {
    setEditingItem(item);
    setItemFormData({ ...item });
    setShowItemForm(true);
  };

  const handleDeleteItem = (index: number) => {
    setContractorItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveItem = () => {
    if (!itemFormData.itm_nm.trim()) {
      showError('Item name is required');
      return;
    }

    // Generate item code if not provided
    let itemCode = itemFormData.itm_cd;
    if (!itemCode) {
      const prefix = itemFormData.itm_type_cd === 'SERVICE' ? 'SVC' :
                     itemFormData.itm_type_cd === 'MACHINERY' ? 'MCH' : 'GDS';
      itemCode = `${prefix}-${String(contractorItems.length + 1).padStart(3, '0')}`;
    }

    const newItem = { ...itemFormData, itm_cd: itemCode };

    if (editingItem) {
      setContractorItems(prev => prev.map(item =>
        item === editingItem ? newItem : item
      ));
    } else {
      setContractorItems(prev => [...prev, newItem]);
    }

    setShowItemForm(false);
    setItemFormData(emptyItem);
  };

  const handleSelectSORItem = (sorItem: SORItem) => {
    setItemFormData({
      ...itemFormData,
      itm_cd: sorItem.sor_itm_cd,
      itm_nm: sorItem.sor_itm_nm,
      uom_cd: sorItem.uom_cd,
      rate_at: sorItem.rate_at,
      sor_itm_id: sorItem.sor_itm_id,
      sor_itm_cd: sorItem.sor_itm_cd
    });
    setShowSORPicker(false);
  };

  // Load SOR items when picker is opened
  const loadSORItems = useCallback(async (search?: string) => {
    setLoadingSOR(true);
    try {
      const items = await contractorsApi.getSORItems?.(search) || [];
      setSorItems(items);
    } catch (error) {
      console.error('Error loading SOR items:', error);
      setSorItems([]);
    } finally {
      setLoadingSOR(false);
    }
  }, []);

  // Handle SOR picker open
  const handleOpenSORPicker = () => {
    setShowSORPicker(true);
    setSorSearchTerm('');
    loadSORItems();
  };

  // Handle SOR search with debounce
  useEffect(() => {
    if (showSORPicker) {
      const timer = setTimeout(() => {
        loadSORItems(sorSearchTerm);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [sorSearchTerm, showSORPicker, loadSORItems]);

  if (!isOpen) return null;

  // Contact form handlers
  const handleAddContact = () => {
    setEditingContact(null);
    // Set as primary if no contacts exist
    const isPrimary = contractorContacts.length === 0;
    setContactFormData({ ...emptyContact, is_prmry_in: isPrimary });
    setShowContactForm(true);
  };

  const handleEditContact = (contact: ContractorContact) => {
    setEditingContact(contact);
    setContactFormData({ ...contact });
    setShowContactForm(true);
  };

  const handleDeleteContact = (index: number) => {
    const contact = contractorContacts[index];
    const updatedContacts = contractorContacts.filter((_, i) => i !== index);

    // If we deleted the primary contact, make the first remaining one primary
    if (contact.is_prmry_in && updatedContacts.length > 0) {
      updatedContacts[0].is_prmry_in = true;
    }

    setContractorContacts(updatedContacts);
  };

  const handleSaveContact = () => {
    if (!contactFormData.cntct_nm.trim()) {
      showError('Contact name is required');
      return;
    }
    if (!contactFormData.phn_nu.trim()) {
      showError('Phone number is required');
      return;
    }

    // Get designation label
    const designation = CONTACT_DESIGNATIONS.find(d => d.code === contactFormData.dsgntn_cd);
    const contactToSave = {
      ...contactFormData,
      dsgntn_nm: designation?.label || contactFormData.dsgntn_nm
    };

    // If setting this as primary, unset others
    let updatedContacts = [...contractorContacts];
    if (contactToSave.is_prmry_in) {
      updatedContacts = updatedContacts.map(c => ({ ...c, is_prmry_in: false }));
    }

    if (editingContact) {
      // Update existing
      updatedContacts = updatedContacts.map(contact =>
        contact === editingContact ? contactToSave : contact
      );
    } else {
      // Add new
      updatedContacts.push(contactToSave);
    }

    setContractorContacts(updatedContacts);
    setShowContactForm(false);
    setContactFormData(emptyContact);
  };

  const handleSetPrimaryContact = (index: number) => {
    const updatedContacts = contractorContacts.map((contact, i) => ({
      ...contact,
      is_prmry_in: i === index
    }));
    setContractorContacts(updatedContacts);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose}></div>

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl transform transition-all">
          {/* Header */}
          <div className={`${WMS_POPUP_HEADER} rounded-t-xl`}>
            <div className="flex items-center gap-2 min-w-0">
              <div className={WMS_POPUP_ICON}>
                <i className="ri-user-settings-line"></i>
              </div>
              <div className="min-w-0">
                <h2 className={WMS_POPUP_TITLE}>
                  {isEdit ? 'Edit Contractor' : 'New Contractor'}
                </h2>
                <p className={WMS_POPUP_SUBTITLE}>
                  {isEdit ? `Editing: ${formData.cntrctr_nm || formData.cntrctr_cd}` : 'Add a new contractor to the system'}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className={WMS_POPUP_CLOSE}
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Tabs */}
          <div className="border-b px-6 bg-gray-50">
            <div className="flex gap-1">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as typeof activeTab)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.key
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[65vh] overflow-y-auto">
            {loading ? (
              <ListSkeleton />
            ) : (
              <>
                {/* Details Tab - All fields grouped */}
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Basic Information Section */}
                    <div className="bg-white">
                      <SectionHeader icon="ri-user-line" title="Basic Information" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contractor Code
                          </label>
                          <input
                            type="text"
                            value={formData.cntrctr_cd}
                            onChange={(e) => handleInputChange('cntrctr_cd', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Auto-generated"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Category
                          </label>
                          <select
                            value={formData.cntrctr_ctgry_id || ''}
                            onChange={(e) => handleInputChange('cntrctr_ctgry_id', parseInt(e.target.value) || null)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                              <option key={cat.cntrctr_ctgry_id} value={cat.cntrctr_ctgry_id}>
                                {cat.cntrctr_ctgry_nm}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Status
                          </label>
                          <select
                            value={formData.cntrctr_sts_id || ''}
                            onChange={(e) => handleInputChange('cntrctr_sts_id', parseInt(e.target.value) || null)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select Status</option>
                            {statuses.map((sts) => (
                              <option key={sts.cntrctr_sts_id} value={sts.cntrctr_sts_id}>
                                {sts.cntrctr_sts_nm}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Contractor Name <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={formData.cntrctr_nm}
                            onChange={(e) => handleInputChange('cntrctr_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Enter contractor/firm name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            PWD Class
                          </label>
                          <select
                            value={formData.pwdclass_cd}
                            onChange={(e) => handleInputChange('pwdclass_cd', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="">Select PWD Class</option>
                            {pwdClasses.map((cls) => (
                              <option key={cls.pwd_cls_id} value={cls.pwd_cls_cd}>
                                {cls.pwd_cls_nm}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>

                    {/* Firm Details Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <SectionHeader icon="ri-building-line" title="Firm Details" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Firm Name
                          </label>
                          <input
                            type="text"
                            value={formData.frm_nm}
                            onChange={(e) => handleInputChange('frm_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="Legal firm/company name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Firm Type
                          </label>
                          <select
                            value={formData.frm_type_id || ''}
                            onChange={(e) => handleInputChange('frm_type_id', parseInt(e.target.value) || null)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          >
                            <option value="">Select Firm Type</option>
                            {firmTypes.map((type) => (
                              <option key={type.frm_type_id} value={type.frm_type_id}>
                                {type.frm_type_nm}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Establishment Date
                          </label>
                          <input
                            type="date"
                            value={formData.estblshmnt_dt}
                            onChange={(e) => handleInputChange('estblshmnt_dt', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Firm Address
                          </label>
                          <textarea
                            value={formData.frm_addr_tx}
                            onChange={(e) => handleInputChange('frm_addr_tx', e.target.value)}
                            rows={2}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="Registered office address"
                          />
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Firm Specializations
                            {selectedSpecializations.length > 0 && (
                              <span className="ml-2 text-blue-600 font-normal">({selectedSpecializations.length} selected)</span>
                            )}
                          </label>
                          <div className="border rounded-lg p-3 max-h-32 overflow-y-auto bg-white">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                              {specializations.map((spec) => (
                                <label
                                  key={spec.frm_spclzn_id}
                                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors text-sm ${
                                    selectedSpecializations.includes(spec.frm_spclzn_id)
                                      ? 'bg-blue-100 border border-blue-300'
                                      : 'bg-gray-50 border border-gray-200 hover:border-gray-300'
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={selectedSpecializations.includes(spec.frm_spclzn_id)}
                                    onChange={() => handleSpecializationToggle(spec.frm_spclzn_id)}
                                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                  />
                                  <span className="text-gray-700">{spec.frm_spclzn_nm}</span>
                                </label>
                              ))}
                            </div>
                            {specializations.length === 0 && (
                              <p className="text-sm text-gray-500 text-center py-2">No specializations available</p>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Address Section */}
                    <div className="bg-white">
                      <SectionHeader icon="ri-map-pin-line" title="Address Information" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-3">
                          <div className="text-sm text-gray-500 mb-3 p-3 bg-blue-50 rounded-lg border border-blue-100 flex items-center gap-2">
                            <i className="ri-information-line text-blue-500"></i>
                            <span>Contact persons can be managed in the <strong>Contacts</strong> tab</span>
                          </div>
                        </div>
                        <div className="md:col-span-3">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Address
                          </label>
                          <textarea
                            value={formData.addr_tx}
                            onChange={(e) => handleInputChange('addr_tx', e.target.value)}
                            rows={2}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="Full address"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            City
                          </label>
                          <input
                            type="text"
                            value={formData.cty_nm}
                            onChange={(e) => handleInputChange('cty_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="City name"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            value={formData.stte_nm}
                            onChange={(e) => handleInputChange('stte_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="State"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            PIN Code
                          </label>
                          <input
                            type="text"
                            value={formData.pin_cd}
                            onChange={(e) => handleInputChange('pin_cd', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                            placeholder="6-digit PIN"
                            maxLength={6}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Government Registration Section */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <SectionHeader icon="ri-government-line" title="Government Registration" />
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Registration Number
                          </label>
                          <input
                            type="text"
                            value={formData.rgstrn_nu}
                            onChange={(e) => handleInputChange('rgstrn_nu', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="PWD/Govt registration"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Registration Date
                          </label>
                          <input
                            type="date"
                            value={formData.rgstrn_dt}
                            onChange={(e) => handleInputChange('rgstrn_dt', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Valid Till
                          </label>
                          <input
                            type="date"
                            value={formData.rgstrn_vld_dt}
                            onChange={(e) => handleInputChange('rgstrn_vld_dt', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            PAN Number {!isEdit && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={formData.pan_nu}
                            onChange={(e) => handleInputChange('pan_nu', e.target.value.toUpperCase())}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white uppercase"
                            placeholder="AAAAA0000A"
                            maxLength={10}
                          />
                          {formData.pan_nu && !PAN_RE.test(formData.pan_nu) && (
                            <p className="text-xs text-red-500 mt-1">Format: AAAAA9999A</p>
                          )}
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            GSTIN
                          </label>
                          <input
                            type="text"
                            value={formData.gstn_nu}
                            onChange={(e) => handleInputChange('gstn_nu', e.target.value.toUpperCase())}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white uppercase"
                            placeholder="15-digit GST number"
                            maxLength={15}
                          />
                          {formData.gstn_nu && formData.gstn_nu.length >= 2 && (
                            (() => {
                              const stateInfo = getGSTINStateInfo(formData.gstn_nu);
                              if (stateInfo) {
                                return (
                                  <p className={`text-xs mt-1 flex items-center gap-1 ${stateInfo.isAP ? 'text-green-600' : 'text-red-600'}`}>
                                    <i className={`${stateInfo.isAP ? 'ri-check-line' : 'ri-information-line'}`}></i>
                                    GSTIN from {stateInfo.isAP ? 'AP' : stateInfo.stateName} state
                                  </p>
                                );
                              }
                              return null;
                            })()
                          )}
                        </div>
                        <div className="md:col-span-3">
                          <div className="flex items-start gap-4">
                            <label className="flex items-center gap-2 cursor-pointer py-2">
                              <input
                                type="checkbox"
                                checked={formData.msme_rgstrd_in}
                                onChange={(e) => handleInputChange('msme_rgstrd_in', e.target.checked)}
                                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                              />
                              <span className="text-sm font-medium text-gray-700">MSME Registered?</span>
                            </label>
                            {formData.msme_rgstrd_in && (
                              <div className="flex-1">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Udyam Registration Number (URN)
                                </label>
                                <input
                                  type="text"
                                  value={formData.udyam_rgstrn_nu}
                                  onChange={(e) => handleInputChange('udyam_rgstrn_nu', e.target.value.toUpperCase())}
                                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white uppercase"
                                  placeholder="UDYAM-XX-00-0000000"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Bank Details Section — where this contractor's bills and advances are paid */}
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <SectionHeader icon="ri-bank-line" title="Bank Details" />
                      <p className="text-xs text-gray-500 -mt-1 mb-3">
                        Running bills, the final bill and advances for this contractor's works are paid into this account.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Account Holder Name {!isEdit && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={formData.bnk_acnt_hldr_nm}
                            onChange={(e) => handleInputChange('bnk_acnt_hldr_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="As printed on the cheque / passbook"
                            maxLength={150}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Account Type
                          </label>
                          <select
                            value={formData.bnk_acnt_type_cd}
                            onChange={(e) => handleInputChange('bnk_acnt_type_cd', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                          >
                            {ACCOUNT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bank Account Number {!isEdit && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={formData.bnk_acnt_nu}
                            onChange={(e) => handleInputChange('bnk_acnt_nu', e.target.value.replace(/[^0-9]/g, ''))}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="9 to 18 digits"
                            maxLength={18}
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Confirm Account Number {!isEdit && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            inputMode="numeric"
                            autoComplete="off"
                            value={confirmAcnt}
                            onChange={(e) => setConfirmAcnt(e.target.value.replace(/[^0-9]/g, ''))}
                            onPaste={(e) => e.preventDefault()}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="Type it again"
                            maxLength={18}
                          />
                          {confirmAcnt && formData.bnk_acnt_nu && confirmAcnt !== formData.bnk_acnt_nu && (
                            <p className="text-xs text-red-500 mt-1">Does not match</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            IFSC Code {!isEdit && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={formData.ifsc_cd}
                            onChange={(e) => handleInputChange('ifsc_cd', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white uppercase"
                            placeholder="SBIN0001234"
                            maxLength={11}
                          />
                          {formData.ifsc_cd && !IFSC_RE.test(formData.ifsc_cd) && (
                            <p className="text-xs text-red-500 mt-1">4 letters, 0, then 6 letters/digits</p>
                          )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bank Name {!isEdit && <span className="text-red-500">*</span>}
                          </label>
                          <input
                            type="text"
                            value={formData.bnk_nm}
                            onChange={(e) => handleInputChange('bnk_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="e.g. State Bank of India"
                            maxLength={150}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Branch
                          </label>
                          <input
                            type="text"
                            value={formData.bnk_brnch_nm}
                            onChange={(e) => handleInputChange('bnk_brnch_nm', e.target.value)}
                            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                            placeholder="Branch name"
                            maxLength={150}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Financial Information Section */}
                    <div className="bg-white">
                      <SectionHeader icon="ri-money-rupee-circle-line" title="Financial Information" />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Max Tender Capacity
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">Rs.</span>
                            <input
                              type="number"
                              value={formData.max_tndr_cpcty_at || ''}
                              onChange={(e) => handleInputChange('max_tndr_cpcty_at', parseFloat(e.target.value) || 0)}
                              className="w-full border rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0.00"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Maximum value of tenders contractor can bid for</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Average Annual Turnover
                          </label>
                          <div className="relative">
                            <span className="absolute left-3 top-2 text-gray-500">Rs.</span>
                            <input
                              type="number"
                              value={formData.avg_trnvr_at || ''}
                              onChange={(e) => handleInputChange('avg_trnvr_at', parseFloat(e.target.value) || 0)}
                              className="w-full border rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              placeholder="0.00"
                            />
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Average of last 3 years</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Services/Items Tab */}
                {activeTab === 'services' && (
                  <div className="space-y-4">
                    {!isEdit ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <i className="ri-save-line text-4xl text-gray-400 mb-3"></i>
                        <p className="text-gray-600">Please save the contractor first to add services/items</p>
                      </div>
                    ) : (
                      <>
                        {/* Header with Add button */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-gray-800">Services, Machinery & Goods</h3>
                            <p className="text-sm text-gray-500">Add items that this contractor can provide</p>
                          </div>
                          <button
                            onClick={handleAddItem}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <i className="ri-add-line"></i>
                            Add Item
                          </button>
                        </div>

                        {/* Items List */}
                        {contractorItems.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <i className="ri-box-3-line text-4xl text-gray-400 mb-3"></i>
                            <p className="text-gray-600 mb-2">No items added yet</p>
                            <p className="text-sm text-gray-500">Add services, machinery or goods that this contractor can provide</p>
                          </div>
                        ) : (
                          <div className="border rounded-lg overflow-hidden">
                            <table data-wms-table className="w-full">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item Code</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">UOM</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Rate</th>
                                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">SOR</th>
                                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200">
                                {contractorItems.map((item, index) => (
                                  <tr key={index} className="hover:bg-gray-50">
                                    <td className="px-4 py-3 text-sm font-mono text-gray-600">{item.itm_cd}</td>
                                    <td className="px-4 py-3 text-sm text-gray-800">{item.itm_nm}</td>
                                    <td className="px-4 py-3">
                                      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                        item.itm_type_cd === 'SERVICE' ? 'bg-blue-100 text-blue-700' :
                                        item.itm_type_cd === 'MACHINERY' ? 'bg-orange-100 text-orange-700' :
                                        'bg-green-100 text-green-700'
                                      }`}>
                                        <i className={ITEM_TYPES.find(t => t.code === item.itm_type_cd)?.icon}></i>
                                        {ITEM_TYPES.find(t => t.code === item.itm_type_cd)?.label}
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-gray-600">{item.uom_cd}</td>
                                    <td className="px-4 py-3 text-sm text-gray-800 text-right">
                                      Rs. {item.rate_at.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                      {item.sor_itm_id ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                                          <i className="ri-link"></i>
                                          {item.sor_itm_cd}
                                        </span>
                                      ) : (
                                        <span className="text-gray-400">-</span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <button
                                        onClick={() => handleEditItem(item)}
                                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        title="Edit"
                                      >
                                        <i className="ri-pencil-line"></i>
                                      </button>
                                      <button
                                        onClick={() => handleDeleteItem(index)}
                                        className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded ml-1"
                                        title="Delete"
                                      >
                                        <i className="ri-delete-bin-line"></i>
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}

                        {/* Item Form Modal */}
                        {showItemForm && (
                          <div className="fixed inset-0 z-[60] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black/30" onClick={() => setShowItemForm(false)}></div>
                            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                              <div className={`${WMS_POPUP_HEADER} rounded-t-lg`}>
                                <h3 className={WMS_POPUP_TITLE}>
                                  {editingItem ? 'Edit Item' : 'Add New Item'}
                                </h3>
                                <button onClick={() => setShowItemForm(false)} className={WMS_POPUP_CLOSE}>
                                  <i className="ri-close-line text-lg"></i>
                                </button>
                              </div>
                              <div className="p-6 space-y-4">
                                {/* Link from SOR button */}
                                <div className="flex justify-end">
                                  <button
                                    onClick={handleOpenSORPicker}
                                    className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
                                  >
                                    <i className="ri-link"></i>
                                    Link from SOR / Rate Contract
                                  </button>
                                </div>

                                {/* Item Type */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-2">Item Type</label>
                                  <div className="grid grid-cols-3 gap-2">
                                    {ITEM_TYPES.map((type) => (
                                      <button
                                        key={type.code}
                                        type="button"
                                        onClick={() => setItemFormData({ ...itemFormData, itm_type_cd: type.code as ContractorItem['itm_type_cd'] })}
                                        className={`p-3 rounded-lg border-2 transition-colors flex flex-col items-center gap-1 ${
                                          itemFormData.itm_type_cd === type.code
                                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                                            : 'border-gray-200 hover:border-gray-300 text-gray-600'
                                        }`}
                                      >
                                        <i className={`${type.icon} text-xl`}></i>
                                        <span className="text-sm font-medium">{type.label}</span>
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {/* Item Code */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Item Code
                                    <span className="text-gray-400 font-normal ml-1">(auto-generated if empty)</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={itemFormData.itm_cd}
                                    onChange={(e) => setItemFormData({ ...itemFormData, itm_cd: e.target.value.toUpperCase() })}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 uppercase"
                                    placeholder="e.g., SVC-001"
                                  />
                                </div>

                                {/* Item Name */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Item Name <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={itemFormData.itm_nm}
                                    onChange={(e) => setItemFormData({ ...itemFormData, itm_nm: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter item/service name"
                                  />
                                </div>

                                {/* UOM and Rate */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Unit of Measure</label>
                                    <select
                                      value={itemFormData.uom_cd}
                                      onChange={(e) => setItemFormData({ ...itemFormData, uom_cd: e.target.value })}
                                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    >
                                      {COMMON_UOMS.map((uom) => (
                                        <option key={uom.code} value={uom.code}>{uom.label} ({uom.code})</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Rate (Rs.)</label>
                                    <input
                                      type="number"
                                      value={itemFormData.rate_at || ''}
                                      onChange={(e) => setItemFormData({ ...itemFormData, rate_at: parseFloat(e.target.value) || 0 })}
                                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="0.00"
                                    />
                                  </div>
                                </div>

                                {/* Description */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                                  <textarea
                                    value={itemFormData.dscn_tx || ''}
                                    onChange={(e) => setItemFormData({ ...itemFormData, dscn_tx: e.target.value })}
                                    rows={2}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Optional description..."
                                  />
                                </div>

                                {/* SOR Link indicator */}
                                {itemFormData.sor_itm_id && (
                                  <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg border border-purple-200">
                                    <i className="ri-link text-purple-600"></i>
                                    <span className="text-sm text-purple-700">
                                      Linked to SOR: <strong>{itemFormData.sor_itm_cd}</strong>
                                    </span>
                                    <button
                                      onClick={() => setItemFormData({ ...itemFormData, sor_itm_id: null, sor_itm_cd: undefined })}
                                      className="ml-auto text-purple-600 hover:text-purple-700"
                                    >
                                      <i className="ri-close-line"></i>
                                    </button>
                                  </div>
                                )}
                              </div>
                              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
                                <button
                                  onClick={() => setShowItemForm(false)}
                                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveItem}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                  {editingItem ? 'Update Item' : 'Add Item'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* SOR Picker Modal */}
                        {showSORPicker && (
                          <div className="fixed inset-0 z-[70] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black/30" onClick={() => setShowSORPicker(false)}></div>
                            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
                              <div className={`${WMS_POPUP_HEADER} rounded-t-lg`}>
                                <h3 className={WMS_POPUP_TITLE}>Select from SOR / Rate Contract</h3>
                                <button onClick={() => setShowSORPicker(false)} className={WMS_POPUP_CLOSE}>
                                  <i className="ri-close-line text-lg"></i>
                                </button>
                              </div>
                              <div className="p-4 border-b">
                                <div className="relative">
                                  <i className="ri-search-line absolute left-3 top-2.5 text-gray-400"></i>
                                  <input
                                    type="text"
                                    value={sorSearchTerm}
                                    onChange={(e) => setSorSearchTerm(e.target.value)}
                                    className="w-full border rounded-lg pl-10 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Search by code or name..."
                                  />
                                </div>
                              </div>
                              <div className="flex-1 overflow-y-auto p-4">
                                {loadingSOR ? (
                                  <ListSkeleton />
                                ) : sorItems.length === 0 ? (
                                  <div className="text-center py-8 text-gray-500">
                                    <i className="ri-file-search-line text-4xl mb-2"></i>
                                    <p>No SOR items found</p>
                                    <p className="text-sm">{sorSearchTerm ? 'Try a different search term' : 'No items available in Schedule of Rates'}</p>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    {sorItems.map((item) => (
                                      <button
                                        key={item.sor_itm_id}
                                        onClick={() => handleSelectSORItem(item)}
                                        className="w-full text-left p-3 rounded-lg border hover:border-blue-300 hover:bg-blue-50 transition-colors"
                                      >
                                        <div className="flex items-center justify-between">
                                          <div>
                                            <span className="font-mono text-sm text-purple-600">{item.sor_itm_cd}</span>
                                            <p className="font-medium text-gray-800">{item.sor_itm_nm}</p>
                                          </div>
                                          <div className="text-right">
                                            <p className="text-xs text-gray-500 uppercase">{item.uom_cd}</p>
                                            <p className="font-medium text-green-600">Rs. {item.rate_at.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                                          </div>
                                        </div>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Contacts Tab */}
                {activeTab === 'contacts' && (
                  <div className="space-y-4">
                    {!isEdit ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <i className="ri-save-line text-4xl text-gray-400 mb-3"></i>
                        <p className="text-gray-600">Please save the contractor first to add contacts</p>
                      </div>
                    ) : (
                      <>
                        {/* Header with Add button */}
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-gray-800">Contact Persons</h3>
                            <p className="text-sm text-gray-500">Add directors, partners, managers and other contact persons</p>
                          </div>
                          <button
                            onClick={handleAddContact}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                          >
                            <i className="ri-add-line"></i>
                            Add Contact
                          </button>
                        </div>

                        {/* Contacts List */}
                        {contractorContacts.length === 0 ? (
                          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                            <i className="ri-contacts-line text-4xl text-gray-400 mb-3"></i>
                            <p className="text-gray-600 mb-2">No contacts added yet</p>
                            <p className="text-sm text-gray-500">Add contact persons such as directors, CEO, partners, etc.</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {contractorContacts.map((contact, index) => (
                              <div
                                key={index}
                                className={`border rounded-lg p-4 ${
                                  contact.is_prmry_in
                                    ? 'border-blue-300 bg-blue-50/50'
                                    : 'border-gray-200 bg-white'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                      contact.is_prmry_in ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                                    }`}>
                                      <i className="ri-user-line text-xl"></i>
                                    </div>
                                    <div>
                                      <div className="flex items-center gap-2">
                                        <h4 className="font-medium text-gray-800">{contact.cntct_nm}</h4>
                                        {contact.is_prmry_in && (
                                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                            Primary
                                          </span>
                                        )}
                                      </div>
                                      {contact.dsgntn_nm && (
                                        <p className="text-sm text-gray-600">{contact.dsgntn_nm}</p>
                                      )}
                                      <div className="flex items-center gap-4 mt-2 text-sm">
                                        <span className="flex items-center gap-1 text-gray-600">
                                          <i className="ri-phone-line text-gray-400"></i>
                                          {contact.phn_nu}
                                        </span>
                                        {contact.alt_phn_nu && (
                                          <span className="flex items-center gap-1 text-gray-600">
                                            <i className="ri-phone-line text-gray-400"></i>
                                            {contact.alt_phn_nu}
                                          </span>
                                        )}
                                        {contact.eml_tx && (
                                          <span className="flex items-center gap-1 text-gray-600">
                                            <i className="ri-mail-line text-gray-400"></i>
                                            {contact.eml_tx}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    {!contact.is_prmry_in && (
                                      <button
                                        onClick={() => handleSetPrimaryContact(index)}
                                        className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                        title="Set as Primary"
                                      >
                                        <i className="ri-star-line"></i>
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleEditContact(contact)}
                                      className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                                      title="Edit"
                                    >
                                      <i className="ri-pencil-line"></i>
                                    </button>
                                    <button
                                      onClick={() => handleDeleteContact(index)}
                                      className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
                                      title="Delete"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Contact Form Modal */}
                        {showContactForm && (
                          <div className="fixed inset-0 z-[60] flex items-center justify-center">
                            <div className="absolute inset-0 bg-black/30" onClick={() => setShowContactForm(false)}></div>
                            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
                              <div className={`${WMS_POPUP_HEADER} rounded-t-lg`}>
                                <h3 className={WMS_POPUP_TITLE}>
                                  {editingContact ? 'Edit Contact' : 'Add New Contact'}
                                </h3>
                                <button onClick={() => setShowContactForm(false)} className={WMS_POPUP_CLOSE}>
                                  <i className="ri-close-line text-lg"></i>
                                </button>
                              </div>
                              <div className="p-6 space-y-4">
                                {/* Contact Name */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Contact Name <span className="text-red-500">*</span>
                                  </label>
                                  <input
                                    type="text"
                                    value={contactFormData.cntct_nm}
                                    onChange={(e) => setContactFormData({ ...contactFormData, cntct_nm: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Enter full name"
                                  />
                                </div>

                                {/* Designation */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Designation
                                  </label>
                                  <select
                                    value={contactFormData.dsgntn_cd}
                                    onChange={(e) => {
                                      const designation = CONTACT_DESIGNATIONS.find(d => d.code === e.target.value);
                                      setContactFormData({
                                        ...contactFormData,
                                        dsgntn_cd: e.target.value,
                                        dsgntn_nm: designation?.label || ''
                                      });
                                    }}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                  >
                                    <option value="">Select Designation</option>
                                    {CONTACT_DESIGNATIONS.map((d) => (
                                      <option key={d.code} value={d.code}>{d.label}</option>
                                    ))}
                                  </select>
                                </div>

                                {/* Phone Numbers */}
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Phone Number <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                      type="tel"
                                      value={contactFormData.phn_nu}
                                      onChange={(e) => setContactFormData({ ...contactFormData, phn_nu: e.target.value })}
                                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="10-digit mobile"
                                      maxLength={10}
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Alternate Phone
                                    </label>
                                    <input
                                      type="tel"
                                      value={contactFormData.alt_phn_nu || ''}
                                      onChange={(e) => setContactFormData({ ...contactFormData, alt_phn_nu: e.target.value })}
                                      className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                      placeholder="Optional"
                                      maxLength={10}
                                    />
                                  </div>
                                </div>

                                {/* Email */}
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Email
                                  </label>
                                  <input
                                    type="email"
                                    value={contactFormData.eml_tx || ''}
                                    onChange={(e) => setContactFormData({ ...contactFormData, eml_tx: e.target.value })}
                                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="email@example.com"
                                  />
                                </div>

                                {/* Primary Contact Checkbox */}
                                <div>
                                  <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={contactFormData.is_prmry_in}
                                      onChange={(e) => setContactFormData({ ...contactFormData, is_prmry_in: e.target.checked })}
                                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span className="text-sm font-medium text-gray-700">Set as Primary Contact</span>
                                  </label>
                                  <p className="text-xs text-gray-500 ml-6 mt-1">
                                    The primary contact will be used for main communications
                                  </p>
                                </div>
                              </div>
                              <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
                                <button
                                  onClick={() => setShowContactForm(false)}
                                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveContact}
                                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                  {editingContact ? 'Update Contact' : 'Add Contact'}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Documents Tab */}
                {activeTab === 'documents' && (
                  <div className="space-y-4">
                    {!isEdit ? (
                      <div className="text-center py-12 bg-gray-50 rounded-lg">
                        <i className="ri-save-line text-4xl text-gray-400 mb-3"></i>
                        <p className="text-gray-600">Please save the contractor first to upload documents</p>
                      </div>
                    ) : (
                      /* The real uploader, the same one ContractorProfile already renders.
                         What stood here was markup only: the dropzone's <input type="file">
                         had no onChange, every card's "Upload" was a bare <button> with no
                         onClick, and "No file uploaded" was hardcoded text that never read
                         anything. Clicking any of it did nothing at all.

                         Its six document types were invented too. The seeded config
                         (CONTRACTOR_DOCS, doc_prcss_id 16) defines NINE -- Registration
                         Certificate, GST, PAN, MSME/Udyam, EMD/Bank Guarantee, License,
                         Insurance, Agreement and Other -- each with its own accepted formats,
                         size limit and custom fields such as certificate number and expiry.
                         The mockup invented "Company Profile" and "Experience Certificates",
                         dropped five real ones, and marked GST optional where the config marks
                         it required. So the form was also telling contractors the wrong thing.

                         Driving both screens from the same config means they cannot drift. */
                      <DocumentUploader handlerCd="CONTRACTOR_DOCS" refId={contractorId!} refNm="contractor" />
                    )}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="border-t px-6 py-4 bg-gray-50 rounded-b-xl flex items-center justify-between">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="ri-check-line"></i>
                    {isEdit ? 'Update Contractor' : 'Create Contractor'}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
